'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import Image from 'next/image';

/**
 * Props for `ValuePropositionGrid`.
 */
export type ValuePropositionGridProps =
  SliceComponentProps<Content.ValuePropositionGridSlice>;

/**
 * Component for "ValuePropositionGrid" Slices.
 */
const ValuePropositionGrid: FC<ValuePropositionGridProps> = ({ slice }) => {
  const { title, subtitle, cta } = slice.primary || {};

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative pt-10 md:pt-20 pb-10 md:pb-20 bg-[#0F423A] overflow-hidden"
    >
      {/* Top Gradient Fade */}
      <div className="absolute top-0 left-0 w-full h-52 md:h-52 z-20 bg-gradient-to-b from-[#0E1010] to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 flex flex-col items-start text-start gap-12">
        <div className="flex flex-col gap-6">
          <PrismicRichText
            field={title}
            components={{
              heading2: ({ children }) => (
                <h2 className="text-4xl text-white font-bold z-20">
                  {children}
                </h2>
              ),
            }}
          />
          <p className="text-md text-white">{subtitle}</p>
          <div className="flex flex-col md:flex-row gap-4">
            <PrismicNextLink field={cta} className="secondary-button w-auto">
              {cta?.text || 'Learn More'}
            </PrismicNextLink>
          </div>

          {/* 2-2 grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mb-12">
            {slice.primary.value_cards &&
              slice.primary.value_cards.length > 0 &&
              slice.primary.value_cards.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col items-start glassy-container p-6 rounded-lg backdrop-blur-md transition-all duration-300 hover:scale-105 hover:bg-white/20"
                >
                  <h3
                    className="text-xl font-semibold text-white mb-2"
                    style={{ color: '#05F273' }}
                  >
                    {item.card_title}
                  </h3>
                  <p className="text-md text-white">{item.card_description}</p>
                </div>
              ))}
          </div>

          {/* Logo Strip */}
          <div className="w-full flex flex-wrap items-center justify-between gap-8">
            {slice.primary.logos &&
              slice.primary.logos.length > 0 &&
              slice.primary.logos.map((logo, index) => (
                <div
                  key={index}
                  className="flex flex-row items-center justify-center p-4"
                >
                  {logo.logo?.url && (
                    <Image
                      src={logo.logo.url}
                      alt={logo.logo.alt ?? ''}
                      width={logo.logo.dimensions?.width ?? 48}
                      height={logo.logo.dimensions?.height ?? 48}
                      className="h-12 w-auto object-contain"
                    />
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuePropositionGrid;
